/* ==========================================================================
   CHLOIA — collection / search listing
   Instant filters + sort + pagination (Section Rendering API) · phone selector
   ========================================================================== */
(() => {
  if (customElements.get('ch-listing')) return;

  const PHONE_KEY = 'chloia:phone';

  const readPhone = () => {
    try {
      const value = JSON.parse(window.localStorage.getItem(PHONE_KEY) || 'null');
      return value && value.brand ? value : { brand: '', model: '' };
    } catch (e) {
      return { brand: '', model: '' };
    }
  };

  const writePhone = (phone) => {
    try {
      if (phone.brand) window.localStorage.setItem(PHONE_KEY, JSON.stringify(phone));
      else window.localStorage.removeItem(PHONE_KEY);
    } catch (e) {}
    document.dispatchEvent(new CustomEvent('ch:phone:change', { detail: phone }));
  };

  // Brand / model passed from search suggestions (?brand=Apple&model=iPhone 16 Pro)
  const params = new URLSearchParams(window.location.search);
  if (params.get('brand')) writePhone({ brand: params.get('brand'), model: params.get('model') || '' });

  class ChListing extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onInput = this.onInput.bind(this);
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('input', this.onInput);
      if (!ChListing.popBound) {
        ChListing.popBound = true;
        window.addEventListener('popstate', () => {
          const el = document.querySelector('ch-listing');
          if (el) el.load(window.location.href, { push: false });
        });
      }
      this.refresh();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('input', this.onInput);
      document.body.classList.remove('ch-lock');
    }

    /* ---------- Data loading ---------- */

    async load(url, { push = true, scroll = false } = {}) {
      const sheet = this.querySelector('[data-ch-sheet]');
      const sheetOpen = sheet && !sheet.hidden;
      const sheetBody = this.querySelector('[data-ch-sheet-body]');
      const sheetScroll = sheetBody ? sheetBody.scrollTop : 0;

      this.classList.add('is-loading');
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      try {
        const target = new URL(url, window.location.origin);
        target.searchParams.set('section_id', this.sectionId);
        const html = await fetch(target.toString(), { signal: this.controller.signal }).then((r) => r.text());
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('ch-listing');
        if (!fresh) throw new Error('No listing');

        if (sheetOpen) this.classList.add('is-refreshing');
        this.innerHTML = fresh.innerHTML;
        this.dataset.activeCount = fresh.dataset.activeCount || '0';

        const clean = new URL(url, window.location.origin);
        clean.searchParams.delete('section_id');
        if (push) window.history.pushState({ chListing: true }, '', clean.pathname + clean.search);

        this.refresh();

        if (sheetOpen) {
          const newSheet = this.querySelector('[data-ch-sheet]');
          const newBody = this.querySelector('[data-ch-sheet-body]');
          if (newSheet) newSheet.hidden = false;
          if (newBody) newBody.scrollTop = sheetScroll;
          requestAnimationFrame(() => this.classList.remove('is-refreshing'));
        }

        if (scroll) {
          const top = this.querySelector('.ch-listing__layout');
          if (top) window.scrollTo({ top: top.getBoundingClientRect().top + window.scrollY - 90, behavior: 'smooth' });
        }

        if (window.Chloia && window.Chloia.Wish) window.Chloia.Wish.sync();
      } catch (e) {
        if (e.name !== 'AbortError') window.location.href = url;
      } finally {
        this.classList.remove('is-loading');
      }
    }

    urlWith(mutator) {
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      url.searchParams.delete('section_id');
      mutator(url.searchParams);
      return url.pathname + url.search;
    }

    /* ---------- Events ---------- */

    onClick(event) {
      const t = event.target;

      const link = t.closest('a[data-ch-filter-link]');
      if (link) {
        event.preventDefault();
        if (link.hasAttribute('data-ch-clear-all')) writePhone({ brand: '', model: '' });
        const href = new URL(link.href, window.location.origin);
        const current = new URLSearchParams(window.location.search).get('sort_by');
        if (current && !href.searchParams.get('sort_by')) href.searchParams.set('sort_by', current);
        this.load(href.pathname + href.search);
        return;
      }

      const page = t.closest('a[data-ch-page-link]');
      if (page) {
        event.preventDefault();
        this.load(page.getAttribute('href'), { scroll: true });
        return;
      }

      const sortChip = t.closest('[data-ch-sort-value]');
      if (sortChip) {
        const value = sortChip.getAttribute('data-ch-sort-value');
        this.load(this.urlWith((p) => p.set('sort_by', value)));
        return;
      }

      if (t.closest('[data-ch-sheet-open]')) {
        const sheet = this.querySelector('[data-ch-sheet]');
        if (sheet) {
          sheet.hidden = false;
          document.body.classList.add('ch-lock');
        }
        return;
      }

      if (t.closest('[data-ch-sheet-close]')) {
        const sheet = this.querySelector('[data-ch-sheet]');
        if (sheet) sheet.hidden = true;
        document.body.classList.remove('ch-lock');
        return;
      }

      const brandBtn = t.closest('[data-ch-phone-brand]');
      if (brandBtn) {
        const brand = brandBtn.getAttribute('data-ch-phone-brand');
        const phone = readPhone();
        writePhone(phone.brand === brand ? { brand: '', model: '' } : { brand, model: '' });
        this.refresh();
        return;
      }

      const modelBtn = t.closest('[data-ch-phone-model]');
      if (modelBtn) {
        const phone = readPhone();
        writePhone({ brand: phone.brand, model: modelBtn.getAttribute('data-ch-phone-model') });
        this.refresh();
        return;
      }

      if (t.closest('[data-ch-phone-chip]')) {
        writePhone({ brand: '', model: '' });
        this.refresh();
      }
    }

    onChange(event) {
      const select = event.target.closest('[data-ch-sort]');
      if (select) {
        this.load(this.urlWith((p) => p.set('sort_by', select.value)));
        return;
      }

      const range = event.target.closest('[data-ch-price]');
      if (range) {
        const param = range.getAttribute('data-param');
        const max = parseFloat(range.getAttribute('data-max'));
        const value = parseFloat(range.value);
        this.load(
          this.urlWith((p) => {
            if (value >= max) p.delete(param);
            else p.set(param, value);
          })
        );
      }
    }

    onInput(event) {
      const range = event.target.closest('[data-ch-price]');
      if (!range) return;
      const label = range.parentElement.querySelector('[data-ch-price-label]');
      if (label && window.Chloia) label.textContent = window.Chloia.money(parseFloat(range.value) * 100);
    }

    /* ---------- Phone state ---------- */

    refresh() {
      const phone = readPhone();

      this.querySelectorAll('[data-ch-phone-brand]').forEach((btn) => {
        const active = btn.getAttribute('data-ch-phone-brand') === phone.brand;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      this.querySelectorAll('[data-ch-phone-models]').forEach((wrap) => {
        wrap.hidden = !phone.brand;
      });

      this.querySelectorAll('[data-ch-phone-models-for]').forEach((group) => {
        group.hidden = group.getAttribute('data-ch-phone-models-for') !== phone.brand;
      });

      this.querySelectorAll('[data-ch-phone-model]').forEach((btn) => {
        const inBrand = btn.closest('[data-ch-phone-models-for]').getAttribute('data-ch-phone-models-for') === phone.brand;
        const active = inBrand && btn.getAttribute('data-ch-phone-model') === phone.model;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      const prefix = this.dataset.notePrefix || 'fits';
      const note = phone.brand ? `· ${prefix} ${phone.brand}${phone.model ? ' ' + phone.model : ''}` : '';
      this.querySelectorAll('[data-ch-phone-note]').forEach((el) => {
        el.textContent = note;
      });

      const chip = this.querySelector('[data-ch-phone-chip]');
      if (chip) {
        chip.hidden = !phone.brand;
        const label = chip.querySelector('[data-ch-phone-chip-label]');
        if (label) label.textContent = phone.brand ? `${phone.brand}${phone.model ? ' · ' + phone.model : ''}` : '';
      }

      const chips = this.querySelector('[data-ch-active-chips]');
      if (chips) chips.hidden = !(chips.getAttribute('data-has-filters') === 'true' || phone.brand);

      const total = parseInt(this.dataset.activeCount || '0', 10) + (phone.brand ? 1 : 0);
      this.querySelectorAll('[data-ch-filter-count]').forEach((el) => {
        el.textContent = total > 0 ? `(${total})` : '';
      });
    }
  }

  customElements.define('ch-listing', ChListing);
})();
